// src/tareas/TableroTareasDepartamento.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Global } from "../helpers/Global";

import {
  Box,
  Stack,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Avatar,
  Chip,
  IconButton,
  Divider,
} from "@mui/material";

import { DataGrid } from "@mui/x-data-grid";

import KanbanUI from "./kanbanUI";
import HistoryIcon from "@mui/icons-material/History";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

/* =========================================================
 *  Constantes UI (columnas Kanban)
 * ======================================================= */
const ESTADOS = [
  { id: "pendiente", titulo: "Pendiente" },
  { id: "en_curso", titulo: "En curso" },
  { id: "resuelta", titulo: "Resuelta" },
  { id: "rechazada", titulo: "Rechazada" },
];

/* =========================================================
 *  ✅ Filtro: ocultar cerradas (resueltas/rechazadas) antiguas
 * ======================================================= */
const DIAS_OCULTAR_CERRADAS = 3;
const MS_DIA = 24 * 60 * 60 * 1000;
const ESTADOS_CERRADOS = new Set(["resuelta", "rechazada"]);

const parseDateSafe = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const debeOcultarCerrada = (t, dias = DIAS_OCULTAR_CERRADAS, ahoraMs = Date.now()) => {
  const estado = String(t?.estado || "").toLowerCase();
  if (!ESTADOS_CERRADOS.has(estado)) return false;

  // Para Kanban usamos última actividad; tu endpoint devuelve fecha_ultima_actualizacion y fecha_creacion
  const dRef = parseDateSafe(t?.fecha_ultima_actualizacion) || parseDateSafe(t?.fecha_creacion);
  if (!dRef) return false;

  return ahoraMs - dRef.getTime() > dias * MS_DIA;
};

/* =========================================================
 *  Auth headers helpers
 * ======================================================= */
const authHeadersJson = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

/* =========================================================
 *  Helpers UI: avatar + fechas + prioridad
 * ======================================================= */
const getAvatarUrl = (usuario) => {
  if (!usuario?.avatar_url) return null;
  return `${Global.url}${usuario.avatar_url}`;
};

const getIniciales = (usuario) => {
  const nombre = (usuario?.nombre || "").trim();
  const apellidos = (usuario?.apellidos || "").trim();
  const n1 = nombre ? nombre.split(" ")[0][0] : "";
  const a1 = apellidos ? apellidos.split(" ")[0][0] : "";
  const combo = (n1 + a1).toUpperCase();
  if (combo) return combo;
  if (nombre) return nombre[0].toUpperCase();
  return "?";
};

const formatearFechaCompleta = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString();
};

const infoFechaVencimiento = (iso) => {
  if (!iso) return { texto: "", colorKey: "default" };

  const fin = new Date(iso);
  const hoy = new Date();
  const msPorDia = 1000 * 60 * 60 * 24;
  const diff = Math.floor((fin - hoy) / msPorDia);

  if (Number.isNaN(diff)) return { texto: String(iso), colorKey: "default" };

  if (diff < 0) {
    const dias = Math.abs(diff);
    const texto = dias === 0 ? "Vencida" : `Vencida hace ${dias} día${dias === 1 ? "" : "s"}`;
    return { texto, colorKey: "error" };
  }

  if (diff === 0) return { texto: "Vence hoy", colorKey: "warning" };
  if (diff === 1) return { texto: "Vence mañana", colorKey: "warning" };
  if (diff <= 7) return { texto: `Vence en ${diff} días`, colorKey: "warning" };

  return { texto: `Vence en ${diff} días`, colorKey: "success" };
};

const prioridadChip = (prioridadRaw) => {
  const p = String(prioridadRaw || "normal").toLowerCase();
  if (p === "critica") return { label: "Crítica", color: "error" };
  if (p === "alta") return { label: "Alta", color: "warning" };
  if (p === "baja") return { label: "Baja", color: "default" };
  return { label: "Normal", color: "info" };
};

/* =========================================================
 *  Componente
 * ======================================================= */
export const TableroTareasDepartamento = () => {
  /* -------------------------------------------------------
   *  Estado: datos base
   * ----------------------------------------------------- */
  const [departamentos, setDepartamentos] = useState([]);
  const [deptoSeleccionado, setDeptoSeleccionado] = useState(null);

  // ✅ Guardamos RAW del endpoint para el listado "Entradas"
  const [tableroRaw, setTableroRaw] = useState([]);

  const [tareasPorEstado, setTareasPorEstado] = useState({});
  const [usuariosMapa, setUsuariosMapa] = useState({});

  // Usuarios del departamento (para reasignar)
  const [usuariosDepto, setUsuariosDepto] = useState([]);

  /* -------------------------------------------------------
   *  Estado: cargas / UX
   * ----------------------------------------------------- */
  const [cargandoDeptos, setCargandoDeptos] = useState(true);
  const [cargandoTablero, setCargandoTablero] = useState(false);
  const [cargandoUsuariosDepto, setCargandoUsuariosDepto] = useState(false);

  // Toast
  const [toast, setToast] = useState({ open: false, type: "info", text: "" });
  const showToast = (type, text) => setToast({ open: true, type, text });

  /* -------------------------------------------------------
   *  Historial (Dialog)
   * ----------------------------------------------------- */
  const [histModalAbierto, setHistModalAbierto] = useState(false);
  const [histCargando, setHistCargando] = useState(false);
  const [histEventos, setHistEventos] = useState([]);
  const [peticionSeleccionada, setPeticionSeleccionada] = useState(null);

  /* -------------------------------------------------------
   *  Panel Gestión (Dialog)  ✅ SOLO para activas (Kanban)
   * ----------------------------------------------------- */
  const [panelAbierto, setPanelAbierto] = useState(false);
  const [peticionPanel, setPeticionPanel] = useState(null);

  const [selectedUsuarioId, setSelectedUsuarioId] = useState("");
  const [nuevoEstado, setNuevoEstado] = useState("");
  const [comentarioAccion, setComentarioAccion] = useState("");
  const [guardandoPanel, setGuardandoPanel] = useState(false);

  /* -------------------------------------------------------
   *  ✅ Entradas departamento (DataGrid)
   * ----------------------------------------------------- */
  const [mostrarEntradasDepto, setMostrarEntradasDepto] = useState(false);
  const [gridQuery, setGridQuery] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  /* -------------------------------------------------------
   *  Util: inicializar columnas Kanban (siempre todas)
   * ----------------------------------------------------- */
  const inicializarEstado = useCallback((items) => {
    const base = {};
    ESTADOS.forEach((e) => (base[e.id] = []));

    (items || []).forEach((item) => {
      const estado = ESTADOS.some((e) => e.id === item.estado) ? item.estado : "pendiente";
      base[estado].push(item);
    });

    setTareasPorEstado(base);
  }, []);

  /* =======================================================
   *  CARGAS
   * ===================================================== */

  // 1) Departamentos del usuario
  useEffect(() => {
    const fetchDeptos = async () => {
      setCargandoDeptos(true);
      try {
        const res = await fetch(`${Global.url}departamentos/mis`, {
          headers: authHeaders(),
        });
        const data = await res.json().catch(() => []);

        if (res.ok && Array.isArray(data)) {
          const activos = data.filter((d) => d.activo !== false);
          setDepartamentos(activos);

          if (activos.length === 1) setDeptoSeleccionado(activos[0].id);
        } else {
          console.error("Error cargando mis departamentos:", data);
          showToast("error", "No se pudieron cargar tus departamentos.");
        }
      } catch (e) {
        console.error("Error cargando mis departamentos:", e);
        showToast("error", "Error de red al cargar departamentos.");
      } finally {
        setCargandoDeptos(false);
      }
    };

    fetchDeptos();
  }, []);

  // 2) Usuarios globales (para avatares/nombres)
  useEffect(() => {
    const fetchUsuarios = async () => {
      try {
        const res = await fetch(`${Global.url}usuarios/todos`, {
          headers: authHeaders(),
        });
        const data = await res.json().catch(() => []);

        if (res.ok && Array.isArray(data)) {
          const mapa = {};
          data.forEach((u) => (mapa[u.id] = u));
          setUsuariosMapa(mapa);
        }
      } catch (e) {
        console.error("Error cargando usuarios:", e);
      }
    };

    fetchUsuarios();
  }, []);

  // 3) Usuarios del departamento (para reasignación)
  useEffect(() => {
    const fetchUsuariosDepto = async () => {
      if (!deptoSeleccionado) {
        setUsuariosDepto([]);
        return;
      }

      setCargandoUsuariosDepto(true);
      try {
        const res = await fetch(`${Global.url}departamentos/${deptoSeleccionado}/usuarios`, {
          headers: authHeaders(),
        });
        const data = await res.json().catch(() => []);

        if (res.ok && Array.isArray(data)) {
          setUsuariosDepto(data.filter((u) => u.miembro_activo !== false));
        } else {
          console.error("Error cargando usuarios del departamento:", data);
          setUsuariosDepto([]);
        }
      } catch (e) {
        console.error("Error cargando usuarios del departamento:", e);
        setUsuariosDepto([]);
      } finally {
        setCargandoUsuariosDepto(false);
      }
    };

    fetchUsuariosDepto();
  }, [deptoSeleccionado]);

  // 4) Tablero (reusable)
  const cargarTableroDepartamento = useCallback(
    async (departamentoIdActual) => {
      if (!departamentoIdActual) {
        setTableroRaw([]);
        inicializarEstado([]);
        return;
      }

      setCargandoTablero(true);
      try {
        const endpoint = `${Global.url}tareas/tablero?scope=departamento&departamentoId=${departamentoIdActual}`;
        const res = await fetch(endpoint, { headers: authHeaders() });
        const data = await res.json().catch(() => []);

        if (!res.ok) {
          console.error("Error al cargar tablero de departamento:", data);
          showToast("error", data.message || "No se pudo cargar el tablero del departamento.");
          setTableroRaw([]);
          inicializarEstado([]);
          return;
        }

        const lista = Array.isArray(data) ? data : [];
        setTableroRaw(lista);

        // ✅ Kanban aplica filtro 3 días (pero la lista 'Entradas' usa raw)
        const filtradasKanban = lista.filter((t) => !debeOcultarCerrada(t, DIAS_OCULTAR_CERRADAS));
        inicializarEstado(filtradasKanban);
      } catch (e) {
        console.error("Error al cargar tablero de departamento:", e);
        showToast("error", "Error de red al cargar el tablero del departamento.");
        setTableroRaw([]);
        inicializarEstado([]);
      } finally {
        setCargandoTablero(false);
      }
    },
    [inicializarEstado]
  );

  // Cargar tablero cuando cambia el departamento
  useEffect(() => {
    if (!deptoSeleccionado) {
      setTableroRaw([]);
      inicializarEstado([]);
      return;
    }
    cargarTableroDepartamento(deptoSeleccionado);
  }, [deptoSeleccionado, cargarTableroDepartamento, inicializarEstado]);

  /* =======================================================
   *  KANBAN: Drag & Drop (optimista + persistencia)
   * ===================================================== */
  const onDragEnd = async (result) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;

    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      return;
    }

    const peticionId = Number(draggableId);
    const nuevo = destination.droppableId;

    // Optimista: reordenar en UI
    setTareasPorEstado((prev) => {
      const copy = {};
      ESTADOS.forEach((e) => (copy[e.id] = [...(prev[e.id] || [])]));

      const origenArr = copy[source.droppableId];
      const destinoArr = copy[destination.droppableId];

      const [movida] = origenArr.splice(source.index, 1);
      if (!movida) return prev;

      movida.estado = nuevo;
      destinoArr.splice(destination.index, 0, movida);

      return copy;
    });

    // Persistir
    try {
      const res = await fetch(`${Global.url}tareas/peticiones/${peticionId}/estado`, {
        method: "PATCH",
        headers: authHeadersJson(),
        body: JSON.stringify({ estado: nuevo }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        console.error("Error al actualizar estado:", data);
        showToast("error", data.message || "No se pudo actualizar el estado.");
        if (deptoSeleccionado) cargarTableroDepartamento(deptoSeleccionado);
      } else {
        showToast("success", "Estado actualizado");
        // ✅ refrescamos raw/kanban para que el listado también se actualice (estado/fechas)
        if (deptoSeleccionado) cargarTableroDepartamento(deptoSeleccionado);
      }
    } catch (e) {
      console.error("Error al actualizar estado:", e);
      showToast("error", "Error de red al actualizar el estado.");
      if (deptoSeleccionado) cargarTableroDepartamento(deptoSeleccionado);
    }
  };

  /* =======================================================
   *  HISTORIAL (por petición)
   * ===================================================== */
  const abrirHistorial = async (tareaPeticion) => {
    const peticionId = tareaPeticion.peticion_id;

    setPeticionSeleccionada(tareaPeticion);
    setHistEventos([]);
    setHistCargando(true);
    setHistModalAbierto(true);

    try {
      const res = await fetch(`${Global.url}peticiones/${peticionId}/historico`, {
        headers: authHeaders(),
      });
      const data = await res.json().catch(() => []);

      if (!res.ok) {
        console.error("Error al cargar historial:", data);
        showToast("error", data.message || "No se pudo cargar el historial.");
        setHistEventos([]);
        return;
      }

      setHistEventos(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Error al cargar historial:", e);
      showToast("error", "Error de red al cargar el historial.");
      setHistEventos([]);
    } finally {
      setHistCargando(false);
    }
  };

  const cerrarHistorial = () => {
    setHistModalAbierto(false);
    setPeticionSeleccionada(null);
    setHistEventos([]);
    setHistCargando(false);
  };

  /* =======================================================
   *  PANEL GESTIÓN (reasignar / estado / comentario)
   *  ✅ se usa desde tarjetas activas (Kanban)
   * ===================================================== */
  const abrirPanelTarea = (tareaPeticion) => {
    setPeticionPanel(tareaPeticion || null);
    setPanelAbierto(true);

    const asigId = tareaPeticion?.asignado_a_usuario_id || null;
    setSelectedUsuarioId(asigId ? String(asigId) : "");
    setNuevoEstado(tareaPeticion?.estado || "pendiente");
    setComentarioAccion("");
  };

  const cerrarPanelTarea = () => {
    setPanelAbierto(false);
    setPeticionPanel(null);
    setSelectedUsuarioId("");
    setNuevoEstado("");
    setComentarioAccion("");
    setGuardandoPanel(false);
  };

  const guardarPanelTarea = async () => {
    if (!peticionPanel) return;

    const peticionId = peticionPanel.peticion_id;

    const asignPrev = peticionPanel.asignado_a_usuario_id || null;
    const asignNuevo = selectedUsuarioId ? Number(selectedUsuarioId) : null;

    const hayCambioAsignacion = asignNuevo !== asignPrev;
    const hayCambioEstado = nuevoEstado && nuevoEstado !== peticionPanel.estado;
    const hayComentario = typeof comentarioAccion === "string" && comentarioAccion.trim().length > 0;

    if (!hayCambioAsignacion && !hayCambioEstado && !hayComentario) {
      showToast("info", "No hay cambios que aplicar.");
      return;
    }

    setGuardandoPanel(true);

    try {
      // 1) Asignación y/o comentario: PATCH /peticiones/:id
      if (hayCambioAsignacion || hayComentario) {
        const body = {
          ...(hayCambioAsignacion ? { asignado_a_usuario_id: asignNuevo } : {}),
          ...(hayComentario ? { comentario: comentarioAccion.trim() } : {}),
        };

        const res = await fetch(`${Global.url}peticiones/${peticionId}`, {
          method: "PATCH",
          headers: authHeadersJson(),
          body: JSON.stringify(body),
        });

        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          console.error("Error al guardar asignación/comentario:", data);
          showToast("error", data.message || "No se pudo guardar la asignación/comentario.");
          setGuardandoPanel(false);
          return;
        }
      }

      // 2) Cambio de estado: PATCH /tareas/peticiones/:id/estado
      if (hayCambioEstado) {
        const body = {
          estado: nuevoEstado,
          ...(hayComentario ? { comentario: comentarioAccion.trim() } : {}),
        };

        const res = await fetch(`${Global.url}tareas/peticiones/${peticionId}/estado`, {
          method: "PATCH",
          headers: authHeadersJson(),
          body: JSON.stringify(body),
        });

        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          console.error("Error al cambiar estado:", data);
          showToast("error", data.message || "No se pudo cambiar el estado.");
          setGuardandoPanel(false);
          return;
        }
      }

      showToast("success", "Cambios aplicados correctamente.");
      cerrarPanelTarea();
      if (deptoSeleccionado) await cargarTableroDepartamento(deptoSeleccionado);
    } catch (e) {
      console.error("Error guardando cambios en el panel:", e);
      showToast("error", "Error de red al guardar los cambios.");
    } finally {
      setGuardandoPanel(false);
    }
  };

  /* =======================================================
   *  Derivados UI
   * ===================================================== */
  const columnas = useMemo(() => ESTADOS, []);
  const nombreDeptoSeleccionado =
    departamentos.find((d) => d.id === deptoSeleccionado)?.nombre || "";

  /* =======================================================
   *  ✅ Entradas: DataGrid rows/columns (desde tableroRaw)
   * ===================================================== */
  const normaliza = (s) =>
    (s ?? "")
      .toString()
      .toLowerCase()
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "");

  const entradasFiltradas = useMemo(() => {
    const q = normaliza(gridQuery);
    const base = Array.isArray(tableroRaw) ? tableroRaw : [];
    if (!q) return base;

    return base.filter((t) => {
      const titulo = normaliza(t?.titulo);
      const desc = normaliza(t?.descripcion);
      const estado = normaliza(t?.estado);
      const pr = normaliza(t?.prioridad);
      const destino = normaliza(t?.destino_label);
      const depto = normaliza(t?.departamento_nombre);

      return (
        titulo.includes(q) ||
        desc.includes(q) ||
        estado.includes(q) ||
        pr.includes(q) ||
        destino.includes(q) ||
        depto.includes(q)
      );
    });
  }, [tableroRaw, gridQuery]);

  const rowsEntradas = useMemo(() => {
    return (entradasFiltradas || []).map((t, idx) => {
      const responsableActual =
        t?.asignado_a_usuario_id && usuariosMapa[t.asignado_a_usuario_id]
          ? usuariosMapa[t.asignado_a_usuario_id]
          : null;

      const responsableNombre = responsableActual
        ? `${responsableActual.nombre || ""} ${responsableActual.apellidos || ""}`.trim()
        : "Sin responsable";

      return {
        id: t?.peticion_id ?? `tmp-${idx}`,
        peticion_id: t?.peticion_id,
        tarea_id: t?.tarea_id,
        titulo: t?.titulo ?? "",
        departamento_nombre: t?.departamento_nombre ?? "",
        destino_label: t?.destino_label ?? "",
        estado: t?.estado ?? "",
        prioridad: t?.prioridad ?? "normal",
        responsable: responsableNombre,
        fecha_creacion: t?.fecha_creacion ?? null,
        fecha_ultima_actualizacion: t?.fecha_ultima_actualizacion ?? null,
        creado_txt: formatearFechaCompleta(t?.fecha_creacion),
        ultima_txt: formatearFechaCompleta(t?.fecha_ultima_actualizacion || t?.fecha_creacion),
        _raw: t,
      };
    });
  }, [entradasFiltradas, usuariosMapa]);

  // ✅ Aquí está el cambio importante:
  // - Quitamos Seguimiento (redundante)
  // - Quitamos Gestionar (solo en activas/Kanban)
  // - Dejamos solo Historial como consulta
  const columnsEntradas = useMemo(
    () => [
      { field: "titulo", headerName: "Título", flex: 1, minWidth: 260 },
      { field: "destino_label", headerName: "Destino", minWidth: 220 },
      {
        field: "prioridad",
        headerName: "Prioridad",
        minWidth: 140,
        renderCell: (params) => {
          const chip = prioridadChip(params?.row?.prioridad);
          return <Chip size="small" label={chip.label} color={chip.color} />;
        },
      },
      { field: "estado", headerName: "Estado", minWidth: 140 },
      { field: "responsable", headerName: "Responsable", minWidth: 220 },
      { field: "ultima_txt", headerName: "Última actividad", minWidth: 200 },
      {
        field: "acciones",
        headerName: "Acciones",
        minWidth: 220,
        flex: 1,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              flexWrap: "wrap",
              overflow: "visible",
            }}
          >
            <Button
              size="small"
              startIcon={<HistoryIcon fontSize="small" />}
              onClick={() =>
                abrirHistorial({
                  peticion_id: params.row.peticion_id,
                  titulo: params.row.titulo,
                })
              }
            >
              Historial
            </Button>
          </Box>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [usuariosMapa]
  );

  /* =======================================================
   *  Render
   * ===================================================== */
  return (
    <Box className="appContainer appContainer--wide">
      <Stack spacing={2}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Tareas por departamento
        </Typography>

        {/* Filtros */}
        <Paper sx={{ p: 2 }}>
          <Box
            sx={{
              display: "grid",
              gap: 2,
              alignItems: "end",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(6, 1fr)" },
            }}
          >
            <FormControl
              size="small"
              fullWidth
              sx={{ gridColumn: { xs: "1 / -1", sm: "span 2", md: "span 3" } }}
              disabled={cargandoDeptos || departamentos.length === 0}
            >
              <InputLabel>Departamento</InputLabel>
              <Select
                label="Departamento"
                value={deptoSeleccionado ?? ""}
                onChange={(e) => setDeptoSeleccionado(e.target.value ? Number(e.target.value) : null)}
              >
                <MenuItem value="">-- Selecciona un departamento --</MenuItem>
                {departamentos.map((d) => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.nombre}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {cargandoDeptos && (
              <Box sx={{ gridColumn: "1 / -1", display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={18} />
                <Typography variant="body2">Cargando departamentos…</Typography>
              </Box>
            )}

            {!cargandoDeptos && departamentos.length === 0 && (
              <Typography variant="body2" sx={{ gridColumn: "1 / -1", color: "text.secondary" }}>
                No perteneces a ningún departamento con tablero de tareas.
              </Typography>
            )}

            {!cargandoDeptos && departamentos.length > 0 && !deptoSeleccionado && (
              <Typography variant="body2" sx={{ gridColumn: "1 / -1", color: "text.secondary" }}>
                Elige un departamento para ver su tablero de tareas.
              </Typography>
            )}
          </Box>
        </Paper>

        {deptoSeleccionado && (
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, fontSize: { xs: "1.05rem", sm: "1.1rem" }, color: "text.primary" }}
          >
            Departamento: {nombreDeptoSeleccionado || "—"}
          </Typography>
        )}

        {/* Tablero */}
        {deptoSeleccionado && (
          <>
            {cargandoTablero ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={18} />
                <Typography variant="body2">Cargando tablero…</Typography>
              </Box>
            ) : (
              <>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Nota: las tareas en “Resuelta” y “Rechazada” con más de {DIAS_OCULTAR_CERRADAS} días desde su última
                  actualización se ocultan del tablero (pero siguen en “Entradas del departamento”).
                </Typography>

                <KanbanUI
                  columns={columnas.map((c) => ({ id: c.id, title: c.titulo }))}
                  itemsByColumn={tareasPorEstado}
                  getItemId={(item) => item.peticion_id}
                  onDragEnd={onDragEnd}
                  renderCard={(tarea) => {
                    const creador = usuariosMapa[tarea.creador_id];
                    const infoFecha = infoFechaVencimiento(tarea.fecha_vencimiento);
                    const pChip = prioridadChip(tarea.prioridad);

                    const responsableActual =
                      tarea.asignado_a_usuario_id && usuariosMapa[tarea.asignado_a_usuario_id]
                        ? usuariosMapa[tarea.asignado_a_usuario_id]
                        : null;

                    const nombreResponsableActual = responsableActual
                      ? `${responsableActual.nombre || ""} ${responsableActual.apellidos || ""}`.trim()
                      : null;

                    return (
                      <>
                        <Stack direction="row" spacing={1.25} sx={{ alignItems: "center" }}>
                          <Avatar
                            src={creador && getAvatarUrl(creador) ? getAvatarUrl(creador) : undefined}
                            sx={{ width: 34, height: 34, fontWeight: 800 }}
                          >
                            {getIniciales(creador || {})}
                          </Avatar>

                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
                              {creador
                                ? `${creador.nombre || ""} ${creador.apellidos || ""}`.trim()
                                : "Creador desconocido"}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                              Creador
                            </Typography>
                          </Box>
                        </Stack>

                        <Typography sx={{ mt: 1, fontWeight: 800 }}>{tarea.titulo}</Typography>

                        {tarea.descripcion && (
                          <Typography sx={{ mt: 0.75, color: "text.secondary" }}>{tarea.descripcion}</Typography>
                        )}

                        <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
                          <Chip size="small" label={pChip.label} color={pChip.color} />

                          {tarea.fecha_vencimiento && infoFecha?.texto ? (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`⏰ ${infoFecha.texto}`}
                              color={infoFecha.colorKey || "default"}
                              title={formatearFechaCompleta(tarea.fecha_vencimiento)}
                            />
                          ) : null}
                        </Stack>

                        <Typography variant="caption" sx={{ display: "block", mt: 1.1, color: "text.secondary" }}>
                          Responsable: {nombreResponsableActual || "sin asignar"}
                        </Typography>

                        {/* ✅ En activas seguimos permitiendo acciones */}
                        <Box sx={{ mt: 1.25, display: "flex", justifyContent: "flex-end", gap: 1, flexWrap: "wrap" }}>
                          <Button
                            size="small"
                            startIcon={<HistoryIcon fontSize="small" />}
                            onClick={() => abrirHistorial(tarea)}
                          >
                            Historial
                          </Button>
                          <Button size="small" onClick={() => abrirPanelTarea(tarea)}>
                            ⚙ Gestionar
                          </Button>
                        </Box>
                      </>
                    );
                  }}
                />

                {/* ============================
                    ✅ Entradas del departamento
                   ============================ */}
                <Paper sx={{ p: 1.5 }}>
                  <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between" }}>
                    <Typography sx={{ fontWeight: 800 }}>Entradas del departamento</Typography>

                    <Button
                      size="small"
                      onClick={() => setMostrarEntradasDepto((v) => !v)}
                      endIcon={mostrarEntradasDepto ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      disabled={!deptoSeleccionado}
                    >
                      {mostrarEntradasDepto ? "Ocultar" : "Mostrar"}
                    </Button>
                  </Stack>

                  {mostrarEntradasDepto && (
                    <Box sx={{ pt: 1 }}>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1.25}
                        sx={{ alignItems: { sm: "center" }, mb: 1 }}
                      >
                        <TextField
                          label="Buscar"
                          value={gridQuery}
                          onChange={(e) => {
                            setGridQuery(e.target.value);
                            setPage(0);
                          }}
                          placeholder="Título, destino, prioridad, estado…"
                          size="small"
                        />

                        <Box sx={{ flex: 1 }} />

                        <Button size="small" onClick={() => deptoSeleccionado && cargarTableroDepartamento(deptoSeleccionado)}>
                          Refrescar
                        </Button>
                      </Stack>

                      <DataGrid
                        autoHeight
                        rows={rowsEntradas}
                        columns={columnsEntradas}
                        disableRowSelectionOnClick
                        pagination
                        paginationModel={{ page, pageSize }}
                        onPaginationModelChange={(m) => {
                          setPage(m.page);
                          setPageSize(m.pageSize);
                        }}
                        pageSizeOptions={[5, 10, 25, 50]}
                        density="compact"
                        sx={{
                          "& .MuiDataGrid-cell": { display: "flex", alignItems: "center" },
                          "& .MuiDataGrid-cellContent": { width: "100%" },
                        }}
                      />
                    </Box>
                  )}
                </Paper>
              </>
            )}
          </>
        )}
      </Stack>

      {/* =====================================================
       *  Toast
       * =================================================== */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.type === "ok" ? "success" : toast.type}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toast.text}
        </Alert>
      </Snackbar>

      {/* =====================================================
       *  Dialog: Historial
       * =================================================== */}
      <Dialog open={histModalAbierto} onClose={cerrarHistorial} fullWidth maxWidth="md">
        <DialogTitle>Historial de petición</DialogTitle>

        <DialogContent dividers>
          <Typography sx={{ mb: 2 }}>
            <strong>Tarea:</strong> {peticionSeleccionada?.titulo}
          </Typography>

          {histCargando ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2">Cargando historial…</Typography>
            </Box>
          ) : histEventos.length === 0 ? (
            <Typography sx={{ fontStyle: "italic" }}>No hay histórico registrado para esta petición.</Typography>
          ) : (
            <Stack spacing={1.5}>
              {histEventos.map((ev) => {
                const usuarioNombre = `${ev.modificado_por_nombre || ""} ${ev.modificado_por_apellidos || ""}`.trim();

                const hayCambioAsignacion = ev.asignado_anterior_id !== null || ev.asignado_nuevo_id !== null;

                const asignadoAnteriorNombre =
                  ev.asignado_anterior_nombre || ev.asignado_anterior_apellidos
                    ? `${ev.asignado_anterior_nombre || ""} ${ev.asignado_anterior_apellidos || ""}`.trim()
                    : "Sin responsable";

                const asignadoNuevoNombre =
                  ev.asignado_nuevo_nombre || ev.asignado_nuevo_apellidos
                    ? `${ev.asignado_nuevo_nombre || ""} ${ev.asignado_nuevo_apellidos || ""}`.trim()
                    : "Sin responsable";

                const esCreacion = String(ev.estado || "").toLowerCase() === "creada";

                return (
                  <Paper key={ev.id} sx={{ p: 1.5 }}>
                    <Stack direction="row" spacing={1.25} sx={{ alignItems: "center" }}>
                      <Avatar sx={{ width: 32, height: 32, fontWeight: 800 }}>
                        {(usuarioNombre ? usuarioNombre.charAt(0) : "•").toUpperCase()}
                      </Avatar>

                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 800, lineHeight: 1.1 }} noWrap>
                          {usuarioNombre || (esCreacion ? "Sistema" : "Usuario desconocido")}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                          {formatearFechaCompleta(ev.fecha_cambio)}
                        </Typography>
                      </Box>
                    </Stack>

                    <Typography sx={{ mt: 1 }}>
                      {esCreacion ? (
                        <>
                          <strong>Creó la tarea</strong>.
                        </>
                      ) : hayCambioAsignacion ? (
                        <>
                          Cambió la asignación de <strong>{asignadoAnteriorNombre}</strong> a{" "}
                          <strong>{asignadoNuevoNombre}</strong>.
                        </>
                      ) : (
                        <>
                          Cambió el estado de <strong>{ev.estado_anterior}</strong> a <strong>{ev.estado}</strong>.
                        </>
                      )}
                    </Typography>

                    {ev.comentario && (
                      <Typography sx={{ mt: 1, fontStyle: "italic", color: "text.secondary" }}>
                        «{ev.comentario}»
                      </Typography>
                    )}
                  </Paper>
                );
              })}
            </Stack>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={cerrarHistorial}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* =====================================================
       *  Dialog: Panel de gestión (solo Kanban)
       * =================================================== */}
      <Dialog open={panelAbierto && !!peticionPanel} onClose={cerrarPanelTarea} fullWidth maxWidth="md">
        <DialogTitle>Gestionar petición #{peticionPanel?.peticion_id}</DialogTitle>

        <DialogContent dividers>
          <Typography sx={{ mb: 0.5 }}>
            <strong>Tarea:</strong> {peticionPanel?.titulo}
          </Typography>
          <Typography sx={{ mb: 0.5 }}>
            <strong>Estado actual:</strong> {peticionPanel?.estado}
          </Typography>
          <Typography sx={{ mb: 2 }}>
            <strong>Responsable actual:</strong>{" "}
            {peticionPanel?.asignado_a_usuario_id && usuariosMapa[peticionPanel.asignado_a_usuario_id]
              ? `${usuariosMapa[peticionPanel.asignado_a_usuario_id].nombre || ""} ${
                  usuariosMapa[peticionPanel.asignado_a_usuario_id].apellidos || ""
                }`.trim()
              : "Sin responsable"}
          </Typography>

          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
              alignItems: "end",
            }}
          >
            <FormControl size="small" fullWidth disabled={cargandoUsuariosDepto || usuariosDepto.length === 0}>
              <InputLabel>Reasignar a</InputLabel>
              <Select label="Reasignar a" value={selectedUsuarioId} onChange={(e) => setSelectedUsuarioId(e.target.value)}>
                <MenuItem value="">Sin responsable</MenuItem>
                {usuariosDepto.map((u) => (
                  <MenuItem key={u.id} value={String(u.id)}>
                    {`${u.nombre || ""} ${u.apellidos || ""}`.trim()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth>
              <InputLabel>Estado</InputLabel>
              <Select label="Estado" value={nuevoEstado} onChange={(e) => setNuevoEstado(e.target.value)}>
                {ESTADOS.map((e) => (
                  <MenuItem key={e.id} value={e.id}>
                    {e.titulo}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              size="small"
              label="Comentario (opcional)"
              value={comentarioAccion}
              onChange={(e) => setComentarioAccion(e.target.value)}
              placeholder="Añade contexto sobre el cambio de estado o la reasignación…"
              multiline
              minRows={3}
              sx={{ gridColumn: "1 / -1" }}
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={cerrarPanelTarea} disabled={guardandoPanel}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={guardarPanelTarea} disabled={guardandoPanel}>
            {guardandoPanel ? "Guardando..." : "Guardar cambios"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TableroTareasDepartamento;
